/* eslint-disable react-hooks/exhaustive-deps */

import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { QuoteContext } from './quoteContext';
import { ConfirmationData } from '../components/common/summary-common/confirmationData';
import { defaultCoverageInformation, updateCoverageInformation } from '../components/application/MX/templates/confirmation/defaultCoverageInformation';
import { overWritePersonalInformationOfTheInsured } from '../components/application/MX/templates/confirmation/defaultPersonalInformationOfTheInsured';
import { overWriteOccupationInformation } from '../components/application/MX/templates/confirmation/defaultOccupationInformation';
import { overWriteIncomeInformation } from '../components/application/MX/templates/confirmation/defaultIncomeInformation';
import { getApplicationSummary } from '../api/applicationService';
import { ApplicationSummary, findValue, updateSummaryPageInfo, getFieldOptionValueByLanguage } from '../components/common/summary-common/applicationSummary';
import { useLocation, useNavigate } from 'react-router-dom';
import { overWritePoliticallyExposedPersonInformation } from '../components/application/MX/templates/confirmation/defaultPoliticallyExposedPersonInformation';
import { overWriteRiskyActivitiesInformation } from '../components/application/MX/templates/confirmation/defaultRiskyActivitiesInformation';
import { overWriteFamilyMedicalHistoryInformation } from '../components/application/MX/templates/confirmation/defaultFamilyMedicalHistoryInformation';
import { overWriteInsuranceInformation } from '../components/application/MX/templates/confirmation/defaultInsuranceInformation';
import { overWriteBeneficiaryInformation } from '../components/application/MX/templates/confirmation/defaultBeneficiaryInformation';
import { overWriteTrustBeneficiaryInformation } from '../components/application/MX/templates/confirmation/defaultTrustBeneficiaryInformation';
import { overWriteMedicalInformation } from '../components/application/MX/templates/confirmation/defaultMedicalInformation';
import { overWritePhysicalInformation } from '../components/application/MX/templates/confirmation/defaultPhysicalInformation';
import { overWritePolicyOwnerInformation } from '../components/application/MX/templates/confirmation/defaultPolicyOwnerInformation';
import { overWriteRelationInformation } from '../components/application/MX/templates/confirmation/defaultRelationInformation';
import { overWritePolicyPurposeInformation } from '../components/application/MX/templates/confirmation/defaultPolicyPurposeInformation';
import { overWriteSubstanceConsumptionInformation } from '../components/application/MX/templates/confirmation/defaultSubstanceConsumptionInformation';
import { overWriteFinancialInformation } from '../components/application/MX/templates/confirmation/defaultFinancialInformation';
import { overWriteKeyPersonQuestionnaireInformation } from '../components/application/MX/templates/confirmation/defaultKeyPersonQuestionnaireInformation';
import { createInitialPaymentSession, createPaymentSession, PaymentSessionResponse } from '../api/paymentService';
import { generarUuid } from '../utils/utils';
import { probeIntakeSession } from '../api/intakeSessionService';
import { createLogger } from '../utils/logger';
import { selectPreferredApplicationSummaryPages } from './applicationSummarySelection';

const logger = createLogger('MxConfirmationContext');

/**
 * Where this screen's data comes from. A plain `isLoading` is not enough: the intake path never
 * writes page blobs, so "no blobs yet" does not mean "still loading". `intake` and `unavailable`
 * are terminal — nothing else is going to bring blobs in later.
 */
export type ConfirmationSummarySource = 'loading' | 'legacy' | 'intake' | 'unavailable';

const combinePagesInOrder = (
    pageIds: string[],
    applicationSummary: ApplicationSummary[],
    overWriteFunction: (json: any, language: "es" | "en" | "pt", isPEP?: boolean, isRisky?: boolean) => Map<string, ConfirmationData>,
    language: "es" | "en" | "pt",
    isPEP: boolean = false,
    isRisky: boolean = false
): Map<string, ConfirmationData> => {
    const orderedPages = new Map();
    pageIds.forEach(id => {
        const page = applicationSummary?.find(page => page.template_page_id === id);
        if (page) {
            orderedPages.set(id, page);
        }
    });

    if (orderedPages.size > 0) {
        const combinedJson = {
            ...orderedPages.values().next().value.json,
            sections: Array.from(orderedPages.values()).flatMap(page => page.json?.sections || [])
        };
        return overWriteFunction(combinedJson, language, isPEP, isRisky);
    }

    return new Map();
};

interface ConfirmationContextType {
    /** Derived from `summarySource`: only `true` while the screen can still end up with data. */
    isLoading: boolean;
    summarySource: ConfirmationSummarySource;
    coverageInformation: Map<string, ConfirmationData>;
    setCoverageInformation: (values: Map<string, ConfirmationData>) => void;
    personalInformationOfTheInsured: Map<string, ConfirmationData>;
    setPersonalInformationOfTheInsured: (values: Map<string, ConfirmationData>) => void;
    occupationInformation: Map<string, ConfirmationData>;
    setOccupationInformation: (values: Map<string, ConfirmationData>) => void;
    incomeInformation: Map<string, ConfirmationData>;
    setIncomeInformation: (values: Map<string, ConfirmationData>) => void;
    politicallyExposedPersonInformation: Map<string, ConfirmationData>;
    setPoliticallyExposedPersonInformation: (values: Map<string, ConfirmationData>) => void;
    riskyActivitiesInformation: Map<string, ConfirmationData>;
    setRiskyActivitiesInformation: (values: Map<string, ConfirmationData>) => void;
    familyMedicalHistoryInformation: Map<string, ConfirmationData>;
    setFamilyMedicalHistoryInformation: (values: Map<string, ConfirmationData>) => void;
    insuranceInformation: Map<string, ConfirmationData>;
    setInsuranceInformation: (values: Map<string, ConfirmationData>) => void;
    beneficiaryInformation: Map<string, ConfirmationData>;
    setBeneficiaryInformation: (values: Map<string, ConfirmationData>) => void;
    medicalInformation: Map<string, ConfirmationData>;
    setMedicalInformation: (values: Map<string, ConfirmationData>) => void;
    physicalInformation: Map<string, ConfirmationData>;
    setPhysicalInformation: (values: Map<string, ConfirmationData>) => void;
    policyOwnerInformation: Map<string, ConfirmationData>;
    setPolicyOwnerInformation: (values: Map<string, ConfirmationData>) => void;
    email: string;
    setEmail: (value: string) => void;
    firstName: string;
    setFirstName: (value: string) => void;
    lastName: string;
    setLastName: (value: string) => void;
    phone: string;
    setPhone: (value: string) => void;
    address: string;
    setAddress: (value: string) => void;
    city: string;
    setCity: (value: string) => void;
    state: string;
    setState: (value: string) => void;
    country: string;
    setCountry: (value: string) => void;
    zipCode: string;
    setZipCode: (value: string) => void;
    backToApplication: () => void;
    getPageIdByTemplateId: (templateId: string) => number;
    findValueByGroupAndQuestion: (groupAndQuestionId: string) => string | undefined;
    findLabelByGroupAndQuestion: (groupAndQuestionId: string) => string | undefined;
    getFieldsBySectionId: (sectionId: string, excludeGroupId?: string) => any[];
    findGroupWithBothConditions: (sectionId: string, questionId1: string, value1: string, questionId2: string, value2: string) => boolean;
    getHealthInsuranceBySectionId: (sectionId: string, excludeGroupId?: string) => Array<{ company: string; coverage_amount: number | null }>;
    getOccupationBySectionId: (sectionId: string, excludeGroupId?: string) => Array<{ name: string; description: string }>;
    relationInformation: Map<string, ConfirmationData>;
    setRelationInformation: (values: Map<string, ConfirmationData>) => void;
    policyPurposeInformation: Map<string, ConfirmationData>;
    setPolicyPurposeInformation: (values: Map<string, ConfirmationData>) => void;
    substanceConsumptionInformation: Map<string, ConfirmationData>;
    setSubstanceConsumptionInformation: (values: Map<string, ConfirmationData>) => void;
    financialInformation: Map<string, ConfirmationData>;
    setFinancialInformation: (values: Map<string, ConfirmationData>) => void;
    keyPersonQuestionnaireInformation: Map<string, ConfirmationData>;
    setKeyPersonQuestionnaireInformation: (values: Map<string, ConfirmationData>) => void;
    getPaymentSession: () => Promise<PaymentSessionResponse>;
    getBeneficiaries: () => any[];
    getFiles: () => Array<{ type: string; file: string; sendToSummary: boolean }>;
    refreshData: () => Promise<void>;
}

const MxConfirmationContext = createContext<ConfirmationContextType>({} as ConfirmationContextType);

export const MxConfirmationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [summarySource, setSummarySource] = useState<ConfirmationSummarySource>('loading');
    const { selected, lastQuote, quoteID } = useContext(QuoteContext);
    const [coverageInformation, setCoverageInformation] = useState<Map<string, ConfirmationData>>(defaultCoverageInformation);
    const [personalInformationOfTheInsured, setPersonalInformationOfTheInsured] = useState<Map<string, ConfirmationData>>(new Map());
    const [occupationInformation, setOccupationInformation] = useState<Map<string, ConfirmationData>>(new Map());
    const [incomeInformation, setIncomeInformation] = useState<Map<string, ConfirmationData>>(new Map());
    const [politicallyExposedPersonInformation, setPoliticallyExposedPersonInformation] = useState<Map<string, ConfirmationData>>(new Map());
    const [riskyActivitiesInformation, setRiskyActivitiesInformation] = useState<Map<string, ConfirmationData>>(new Map());
    const [familyMedicalHistoryInformation, setFamilyMedicalHistoryInformation] = useState<Map<string, ConfirmationData>>(new Map());
    const [insuranceInformation, setInsuranceInformation] = useState<Map<string, ConfirmationData>>(new Map());
    const [beneficiaryInformation, setBeneficiaryInformation] = useState<Map<string, ConfirmationData>>(new Map());
    const [medicalInformation, setMedicalInformation] = useState<Map<string, ConfirmationData>>(new Map());
    const [physicalInformation, setPhysicalInformation] = useState<Map<string, ConfirmationData>>(new Map());
    const [policyOwnerInformation, setPolicyOwnerInformation] = useState<Map<string, ConfirmationData>>(new Map());
    const [relationInformation, setRelationInformation] = useState<Map<string, ConfirmationData>>(new Map());
    const [financialInformation, setFinancialInformation] = useState<Map<string, ConfirmationData>>(new Map());
    const [policyPurposeInformation, setPolicyPurposeInformation] = useState<Map<string, ConfirmationData>>(new Map());
    const [substanceConsumptionInformation, setSubstanceConsumptionInformation] = useState<Map<string, ConfirmationData>>(new Map());
    const [keyPersonQuestionnaireInformation, setKeyPersonQuestionnaireInformation] = useState<Map<string, ConfirmationData>>(new Map());
    const [applicationSummary, setApplicationSummary] = useState<ApplicationSummary[]>([]);
    const [valuesCache, setValuesCache] = useState<Map<string, string | undefined>>(new Map());
    const [email, setEmail] = useState<string>('');
    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [middleName, setMiddleName] = useState<string>('');
    const [phone, setPhone] = useState<string>('');
    const [address, setAddress] = useState<string>('');
    const [city, setCity] = useState<string>('');
    const [state, setState] = useState<string>('');
    const [country, setCountry] = useState<string>('');
    const [zipCode, setZipCode] = useState<string>('');
    const [typePerson, setTypePerson] = useState<string>('');
    const language: "es" | "en" | "pt" = 'es';
    const currency: string = 'MXN $';
    const navigate = useNavigate();
    const { pathname } = useLocation();
    // Confirmation and attachment are the only screens that read this summary.
    // On /application (chat and wizard) the retry loop below is pure traffic:
    // an empty legacy summary probes the intake session and waits 1.5s, up to
    // five times, before the first chat message can get a free connection.
    const loadsLegacySummary =
        pathname.includes('/confirmation') || pathname.includes('/attachment');

    const backToApplication = () => {
        navigate(`/application?quoteID=${quoteID}`);
    }

    const getPageIdByTemplateId = (templateId: string): number => {
        const page = applicationSummary?.find((page) => page.template_page_id === templateId);
        return page ? page.id : 0;
    };

    // Legacy builds the summary header from quote data; until that lands there is nothing to
    // render. The other sources are already terminal.
    const coverageReady = useMemo(
        () => JSON.stringify([...coverageInformation]) !== JSON.stringify([...defaultCoverageInformation]),
        [coverageInformation],
    );
    const isLoading = summarySource === 'loading' || (summarySource === 'legacy' && !coverageReady);

    useEffect(() => {
        if (selected && lastQuote) {
            setCoverageInformation(updateCoverageInformation(selected, lastQuote, coverageInformation));
        }
    }, [selected, lastQuote]);

    useEffect(() => {
        if (quoteID && loadsLegacySummary) {
            fetchApplicationSummary(quoteID);
        }
    }, [quoteID, loadsLegacySummary]);

    // Función para forzar la recarga de datos
    const refreshData = async () => {
        if (quoteID) {
            // Solo poner en loading si realmente no tenemos datos aún
            // Evitamos un pantallazo de carga cuando ya hay datos en pantalla (ej: al volver de attachment)
            if (!applicationSummary || applicationSummary.length === 0) {
                setSummarySource('loading');
            }
            await fetchApplicationSummary(quoteID);
        }
    };

    useEffect(() => {
        const loadApplicationData = async () => {
            if (applicationSummary) {
                const personalInformationPageIds = [
                    '8ccc6d0f-d7b4-4665-9ee0-95ca2fb8feef', // PAGE_INSURED_INFORMATION
                    'fb4ad844-eff9-428b-a887-3c4dc3d3cbee', // PAGE_INSURED_INFORMATION_ADDITIONAL
                ];

                setPersonalInformationOfTheInsured(
                    combinePagesInOrder(
                        personalInformationPageIds,
                        applicationSummary,
                        (json, lang, pep, risky) => updateSummaryPageInfo(json, overWritePersonalInformationOfTheInsured, lang, pep, risky),
                        language,
                        false,
                        false
                    )
                );

                const pagePolicyOwnerInformation = applicationSummary.find((page) => page.template_page_id === "ceebf9a4-9371-4a7e-a4c5-c57bec63daab");
                setPolicyOwnerInformation(updateSummaryPageInfo(pagePolicyOwnerInformation?.json, overWritePolicyOwnerInformation, language));

                const pageOccupationInformation = applicationSummary.find((page) => page.template_page_id === "fc9f51bb-76ea-48e4-a9f8-3c3c394e4a50");
                setOccupationInformation(updateSummaryPageInfo(pageOccupationInformation?.json, overWriteOccupationInformation, language, false, false, { "9b65fd68-c0db-402f-aa1b-281b083c1ece": "Ocupação" }));

                const pageIncomeInformation = applicationSummary.find((page) => page.template_page_id === "931e3f94-e788-41c8-9c78-44a66d479e3f");
                setIncomeInformation(updateSummaryPageInfo(pageIncomeInformation?.json, overWriteIncomeInformation, language, undefined, undefined, undefined, currency));

                const pagePoliticallyExposedPersonInformation = applicationSummary.find((page) => page.template_page_id === "2743cd1a-14f5-465a-8338-68550601a91e");
                setPoliticallyExposedPersonInformation(updateSummaryPageInfo(pagePoliticallyExposedPersonInformation?.json, overWritePoliticallyExposedPersonInformation, language, true, false));

                const pagePhysicalInformation = applicationSummary.find((page) => page.template_page_id === "c641c8cb-4113-4952-b6e2-fa6062a1346a");
                setPhysicalInformation(updateSummaryPageInfo(pagePhysicalInformation?.json, overWritePhysicalInformation, language, true, false, {
                    "c945a3f8-c08c-4bf9-a918-498baa790a29": "Substância",
                }));

                const riskyActivitiesPageIds = [
                    "b45c6834-f489-4759-b656-e8bf039ca94f",
                    "f6f65daa-285c-4de4-875b-a06966e6ba33",
                    "92dde657-c33f-40f5-b4b6-3069628f35d6",
                    "d7d003e8-29bd-429a-b0f8-cd4bb87f9283",
                    "8480b7c5-ba9f-4567-bc71-55d658b5f31f",
                    "32020c0d-ef1e-4c58-b156-9976633c7529",
                    "ad9252f6-42b9-4d21-8aad-2719ad55a0aa",
                    "e118e0a2-c65c-4b4a-b7ff-b689c3d09166",
                    "458c1997-02ae-4ad3-83de-f3ace9e745e3",
                    "e798133e-47d5-4522-93f9-deaf0665ea35"
                ];

                setRiskyActivitiesInformation(
                    combinePagesInOrder(
                        riskyActivitiesPageIds,
                        applicationSummary,
                        (json, lang, pep, risky) => updateSummaryPageInfo(json, overWriteRiskyActivitiesInformation, lang, pep, risky, {
                            "dd333cb6-a6ce-4966-bed9-0e756f57c0e8": "Certificación",
                            "7d86ff28-4258-4a23-a58b-4e3bc6bca6f4": "Certificación de especialidad",
                            "a2fa9250-01ad-4407-9135-95752f36a441": "Tipo de buceo",
                            "2c15ca10-3702-4383-bde7-9436990f88a4": "Locación",
                            "e2919699-2110-4b6c-a17f-a2be76735317": "Locación",
                        }),
                        language,
                        true,
                        false
                    )
                );

                const medicalPageIds = [
                    "5cd6641e-0210-4ee1-849e-c36c38dd89b9",
                    "f2f55277-35c2-419a-81ac-a95cd4f635ef",
                    "b4770b92-b466-4baf-ac06-d775e424f324",

                    "870d6a6b-fedf-4ab5-accc-3196a8f0c383",
                    "7d092174-25dc-41fa-97c1-538432f5c53d",

                    "d7f590b5-7b4f-4a0b-8448-ec4e3f1f6499",
                    "5e3cd85d-5e22-47c7-aa2a-42853ba23448",
                    "b969575d-308a-492b-9f5c-1c7a6e54b780",
                    "43e0270a-be8e-4fde-bd8d-1e14a0ebfc5a",

                    "a3381be7-c381-41a3-8ac8-0b18ad7f450e",

                    "6640e390-398c-4cf5-8da9-e0b7182f0149",
                    "31eccdff-0b46-42e4-b980-2c4e49074a7b",

                    "579ade1e-832c-4803-99c9-49cd949bc3f1",

                    "4ba0149e-12c9-4ff0-9dd4-75ad64d1fb5d",

                    "41e218a4-c523-4b85-b37b-589c59daee94",
                    "39566c71-24da-424b-8605-9a9f16d5a928",
                    "37fe8465-e7f5-4b26-8d29-1786f6ffc315",
                    "4c7b4dd7-b770-4883-a202-548c3162ec94",
                    "65ad8558-3135-4e2e-b500-f48f9f8ed7ad",

                    "22488942-0417-4630-84fc-53d681be8fd8",

                    "7e57f614-366d-49c5-80f5-72eb4389e8e7",
                    "3f5ba244-79ce-43e1-85aa-7b1c94785525",
                    "d9ecbb27-7603-4cb8-be14-9c8e188e93e7",
                    "3afa143c-8712-4c01-8e36-2cac8ff899e2",

                    "be184cc0-f829-4312-a3ed-c579d51123ed",
                    "9c1b0c3b-814d-471c-a29a-1ae59cde4959",
                    "17324673-0e54-4564-81e8-3f50a8e1f56b",

                    "49d0fd9d-0c69-4a75-9be5-292ac8e05c79",

                    "6105330c-5aae-4a7b-b597-aa84f7e573b5",
                ];

                setMedicalInformation(
                    combinePagesInOrder(
                        medicalPageIds,
                        applicationSummary,
                        (json, lang, pep, risky) => updateSummaryPageInfo(json, overWriteMedicalInformation, lang, pep, risky),
                        language,
                        true,
                        false
                    )
                );


                const pageFamilyMedicalHistoryInformation = applicationSummary.find((page) => page.template_page_id === "855c7ed6-50de-4dae-a936-e5dfb50d66e5");
                setFamilyMedicalHistoryInformation(updateSummaryPageInfo(pageFamilyMedicalHistoryInformation?.json, overWriteFamilyMedicalHistoryInformation, language, true, false));

                const pageInsuranceInformation = applicationSummary.find((page) => page.template_page_id === "6cc602c4-1df2-4448-b133-b57b2a22012f");
                setInsuranceInformation(updateSummaryPageInfo(pageInsuranceInformation?.json, overWriteInsuranceInformation, language, true, false, {
                    "813e1cf6-e9e1-4bc0-81e9-90acd0fc55f5": "Seguro de vida"
                }, currency));

                // Beneficiarios: usar Key Person Beneficiaries cuando existe Q1, sino la página regular o trust
                const hasKeyPersonQuestionnaire = applicationSummary.some(
                    p => p.template_page_id === '6d757fb1-5bed-4430-920f-56f73e3dd039'
                );

                if (hasKeyPersonQuestionnaire) {
                    const keyPersonBeneficiariesPage = applicationSummary.find(
                        p => p.template_page_id === '1cf974cb-1d7d-4480-83d3-dcd398b108d8'
                    );
                    if (keyPersonBeneficiariesPage?.json?.sections) {
                        const beneficiaryMap = new Map<string, ConfirmationData>();
                        beneficiaryMap.set('key-person-beneficiary-title', { label: 'Beneficiario Principal', fontWeight: 'bold', showBorder: true });
                        for (const section of keyPersonBeneficiariesPage.json.sections) {
                            for (const group of section.groups || []) {
                                for (const field of group.fields || []) {
                                    if (field.ui === 'label') {
                                        const title = field.title?.es || '';
                                        const value = field.value || field.value_default || '';
                                        if (title.endsWith(':') && value) {
                                            beneficiaryMap.set(
                                                `${group.group_id}|${field.question_id}`,
                                                { label: title.replace(/:$/, ''), value, showBorder: true }
                                            );
                                        }
                                    }
                                }
                            }
                        }
                        setBeneficiaryInformation(beneficiaryMap);
                    } else {
                        setBeneficiaryInformation(new Map());
                    }
                } else {
                    const pageBeneficiaryInformation = applicationSummary.find((page) => page.template_page_id === "fbb67cc4-463c-45b4-9bde-1ff842782d60");

                    if (pageBeneficiaryInformation) {
                        setBeneficiaryInformation(updateSummaryPageInfo(pageBeneficiaryInformation?.json, overWriteBeneficiaryInformation, language, true, false, {
                            "e7486edd-7b28-403c-99b9-be59e155d046": "Beneficiario",
                        }));
                    } else {
                        const pageTrustBeneficiaryInformation = applicationSummary.find((page) => page.template_page_id === "45b1f7aa-6a90-4567-b549-3c9708a03124");
                        if (pageTrustBeneficiaryInformation) {
                            setBeneficiaryInformation(updateSummaryPageInfo(pageTrustBeneficiaryInformation?.json, overWriteTrustBeneficiaryInformation, language, true, false, {
                                "96a1dd0d-3315-40de-983c-dc61a73a3f13": "Beneficiario Contingente",
                            }));
                        }
                    }
                }

                // Relación
                const pageRelationInformation = applicationSummary.find((page) => page.template_page_id === "b35a354f-b268-49c5-89d9-59c4b7ddbce1");
                setRelationInformation(updateSummaryPageInfo(pageRelationInformation?.json, overWriteRelationInformation, language, true, false));

                // Propósito de la póliza (Credit Guarantee)
                const pagePolicyPurposeInformation = applicationSummary.find((page) => page.template_page_id === "c90f7865-5fb4-4511-a031-8777431c8f12");
                setPolicyPurposeInformation(updateSummaryPageInfo(pagePolicyPurposeInformation?.json, overWritePolicyPurposeInformation, language, true, false, undefined, currency));

                // Consumo de sustancias (Drugs)
                const pageSubstanceConsumptionInformation = applicationSummary.find((page) => page.template_page_id === "9562c49a-088e-43f7-80b5-976f03a59cee");
                setSubstanceConsumptionInformation(updateSummaryPageInfo(pageSubstanceConsumptionInformation?.json, overWriteSubstanceConsumptionInformation, language, true, false));


                const financialPageIds = [
                    "f89bf9e3-9bb9-48ca-b539-d4da6838c9c1",
                    "04759c3b-b371-4dc7-ad90-da6de5d15fee",
                ];

                // Combinar páginas financieras manualmente para agregar currency
                const financialPages = financialPageIds
                    .map(id => applicationSummary.find(page => page.template_page_id === id))
                    .filter(page => page !== undefined);

                if (financialPages?.length > 0) {
                    const combinedFinancialJson = {
                        ...financialPages[0].json,
                        sections: financialPages.flatMap(page => page.json?.sections || [])
                    };
                    setFinancialInformation(updateSummaryPageInfo(combinedFinancialJson, overWriteFinancialInformation, language, true, false, undefined, currency));
                } else {
                    setFinancialInformation(new Map());
                }

                // Cuestionario de persona clave (Q1 + Q2 + Q3)
                const keyPersonPageIds = [
                    '6d757fb1-5bed-4430-920f-56f73e3dd039', // PAGE_KEY_PERSON_QUESTIONNAIRE (Q1)
                    '38c808b8-b7a7-4dec-b199-e276aa8263a6', // PAGE_KEY_PERSON_QUESTIONNAIRE_2 (Q2)
                    'a01720fd-9e54-45b4-a600-8b42ba8b003f', // PAGE_KEY_PERSON_QUESTIONNAIRE_3 (Q3)
                ];

                setKeyPersonQuestionnaireInformation(
                    combinePagesInOrder(
                        keyPersonPageIds,
                        applicationSummary,
                        (json, lang) => updateSummaryPageInfo(json, overWriteKeyPersonQuestionnaireInformation, lang),
                        language
                    )
                );

                const pagePersonalInformationOfTheInsured = applicationSummary.find((page) => page.template_page_id === '8ccc6d0f-d7b4-4665-9ee0-95ca2fb8feef');
                if (pagePersonalInformationOfTheInsured) {
                    setEmail(findValue(pagePersonalInformationOfTheInsured?.json, 'eadd03c1-8434-41d7-acc1-59bec968fd14', 'edd2330d-cdbb-4f36-a698-7b243187ac9b', "a1c1f62e-326b-46f0-bb7d-33d436ca267d"));
                    setFirstName(findValue(pagePersonalInformationOfTheInsured?.json, 'eadd03c1-8434-41d7-acc1-59bec968fd14', 'edd2330d-cdbb-4f36-a698-7b243187ac9b', "773e8c82-3c69-4745-8520-43ac32b1b030"));
                    setMiddleName(findValue(pagePersonalInformationOfTheInsured?.json, 'eadd03c1-8434-41d7-acc1-59bec968fd14', 'edd2330d-cdbb-4f36-a698-7b243187ac9b', "6fe7ea08-6360-4d02-8fe5-3da568536456"));
                    const lastName = findValue(pagePersonalInformationOfTheInsured?.json, 'eadd03c1-8434-41d7-acc1-59bec968fd14', 'edd2330d-cdbb-4f36-a698-7b243187ac9b', "60d2747d-e39e-4026-a98d-960a06d8fa72");
                    const lastName2 = findValue(pagePersonalInformationOfTheInsured?.json, 'eadd03c1-8434-41d7-acc1-59bec968fd14', 'edd2330d-cdbb-4f36-a698-7b243187ac9b', "f486af1f-5859-46de-9372-2d157e6df8be");
                    setLastName(lastName + " " + lastName2);
                    setPhone(findValue(pagePersonalInformationOfTheInsured?.json, 'eadd03c1-8434-41d7-acc1-59bec968fd14', 'edd2330d-cdbb-4f36-a698-7b243187ac9b', "103a15f4-0d02-496a-bf0f-994ee9e4b945"));

                    let addressName = findValue(pagePersonalInformationOfTheInsured?.json, 'cf8bdea1-6f0d-402c-bab3-93719e219e9c', 'f7b71ece-c6da-4d7f-95a4-03cbdcea4eec', "7cc74887-0995-4537-901b-530eb1c4fd13");
                    let addressNumber = findValue(pagePersonalInformationOfTheInsured?.json, 'cf8bdea1-6f0d-402c-bab3-93719e219e9c', 'f7b71ece-c6da-4d7f-95a4-03cbdcea4eec', "7280ae10-8ada-44b6-a2ab-2d7a8b71cbd1");

                    setAddress(addressName + " " + addressNumber);
                    setCity(findValue(pagePersonalInformationOfTheInsured?.json, 'cf8bdea1-6f0d-402c-bab3-93719e219e9c', 'f7b71ece-c6da-4d7f-95a4-03cbdcea4eec', "b670202f-15aa-4ac1-bb6f-57e2cfc77d14"));
                    setState(findValue(pagePersonalInformationOfTheInsured?.json, 'cf8bdea1-6f0d-402c-bab3-93719e219e9c', 'f7b71ece-c6da-4d7f-95a4-03cbdcea4eec', "1f36fa8b-b5f1-4762-8381-b444f75d3940"));
                    setCountry('MX');
                    const isMexican = findValue(pagePersonalInformationOfTheInsured?.json, 'eadd03c1-8434-41d7-acc1-59bec968fd14', 'edd2330d-cdbb-4f36-a698-7b243187ac9b', "edd3d6bf-dcc8-4d2d-8eac-797416c00399") === 'MX';
                    const documentType = isMexican ?
                        findValue(pagePersonalInformationOfTheInsured?.json, 'eadd03c1-8434-41d7-acc1-59bec968fd14', 'edd2330d-cdbb-4f36-a698-7b243187ac9b', "5bb8d0cc-e0b2-4141-9315-3b4f57ba5a86") :
                        findValue(pagePersonalInformationOfTheInsured?.json, 'eadd03c1-8434-41d7-acc1-59bec968fd14', 'edd2330d-cdbb-4f36-a698-7b243187ac9b', "e7e62719-9c88-4c77-8623-d338285951dc");
                    setTypePerson(documentType || '');
                }
            }
        };

        if (applicationSummary) {
            loadApplicationData();
        }
    }, [applicationSummary]);

    useEffect(() => {
        if (applicationSummary?.length > 0) {
            // Construir el cache una sola vez cuando applicationSummary cambie
            buildValuesCache();
        }
    }, [applicationSummary]);

    const buildValuesCache = () => {
        const cache = new Map<string, string | undefined>();

        for (const page of applicationSummary) {
            if (!page.json?.sections) continue;

            for (const section of page.json.sections) {
                if (!section.groups) continue;

                for (const group of section.groups) {
                    if (group.hidden) continue;

                    if (group.fields) {
                        for (const field of group.fields) {
                            if (field.hidden) continue;

                            const key = `${group.group_id}|${field.question_id}`;
                            const value = field.value || field.value_default;
                            cache.set(key, value);
                        }
                    }
                }
            }
        }

        setValuesCache(cache);
    };

    const findValueByGroupAndQuestion = (groupAndQuestionId: string): string | undefined => {
        // Verificar si el cache está construido
        if (valuesCache.size === 0 && applicationSummary?.length > 0) {
            // Si el cache no está listo, construirlo sincrónicamente
            const cache = new Map<string, string | undefined>();

            for (const page of applicationSummary) {
                if (!page.json?.sections) continue;

                for (const section of page.json.sections) {
                    if (!section.groups) continue;

                    for (const group of section.groups) {
                        if (group.hidden) continue;

                        if (group.fields) {
                            for (const field of group.fields) {
                                if (field.hidden) continue;

                                const key = `${group.group_id}|${field.question_id}`;
                                const value = field.value || field.value_default;
                                cache.set(key, value);
                            }
                        }
                    }
                }
            }

            setValuesCache(cache);
            return cache.get(groupAndQuestionId);
        }

        // Usar el cache si está disponible
        return valuesCache.get(groupAndQuestionId);
    };

    const findLabelByGroupAndQuestion = (groupAndQuestionId: string): string | undefined => {
        const [groupId, questionId] = groupAndQuestionId.split('|');
        if (!groupId || !questionId) return undefined;

        for (const page of applicationSummary) {
            if (!page.json?.sections) continue;
            for (const section of page.json.sections) {
                if (!section.groups) continue;
                for (const group of section.groups) {
                    if (group.group_id !== groupId) continue;
                    if (!group.fields) continue;
                    for (const field of group.fields) {
                        if (field.question_id !== questionId) continue;
                        const value = field.value || field.value_default;
                        if (!value || !field.options?.length) return value;
                        return getFieldOptionValueByLanguage(field, value, language) || value;
                    }
                }
            }
        }
        return undefined;
    };

    // getApplicationSummary throws on non-OK responses; without this a 500 left the promise
    // rejected and the screen loading forever.
    const readApplicationSummary = async (quoteID: string): Promise<ApplicationSummary[]> => {
        try {
            return (await getApplicationSummary(quoteID)) || [];
        } catch (error) {
            logger.error('Could not read the application summary:', error);
            return [];
        }
    };

    const fetchApplicationSummary = async (quoteID: string) => {
        let summary = await readApplicationSummary(quoteID);
        let attempts = 0;
        let hasIntakeSession = false;

        // Reintentar solo si el resumen viene vacío (desfase de sincronización al guardar)
        while (summary.length === 0 && attempts < 5) {
            // Intake schema path never dual-writes page blobs — don't wait ~7.5s for sync that won't happen
            const probe = await probeIntakeSession(quoteID, { fresh: attempts > 0 });
            if (probe.status === 'present') {
                hasIntakeSession = true;
                break;
            }

            await new Promise(resolve => setTimeout(resolve, 1500));
            summary = await readApplicationSummary(quoteID);
            attempts++;
        }

        const preferredSummary = selectPreferredApplicationSummaryPages(summary);
        setApplicationSummary(preferredSummary);
        // No blobs and no intake session means there is nowhere left to read from: that is a
        // failure, not a wait. Marking it keeps consumers from hanging on `isLoading`.
        setSummarySource(preferredSummary.length > 0 ? 'legacy' : hasIntakeSession ? 'intake' : 'unavailable');
    };

    const getFieldsBySectionId = (sectionId: string, excludeGroupId?: string): any[] => {
        const results: any[] = [];

        // IDs de las preguntas de control
        const controlQuestionId1 = "7f842c4f-b6a1-464e-90c3-2ff9ee750b3b";
        const controlQuestionId2 = "624f039a-a4ba-4494-b126-f4ba8664089e";

        if (!applicationSummary) return results;

        for (const page of applicationSummary) {
            if (!page.json?.sections) continue;

            for (const section of page.json.sections) {
                if (section.section_id === sectionId) {
                    if (!section.groups) continue;

                    for (const group of section.groups) {
                        if (group.hidden) continue;
                        if (excludeGroupId && group.group_id === excludeGroupId) continue;

                        // Obtener valores de control para este grupo específico
                        let field1Value = "";
                        let field2Value = "";

                        if (group.fields) {
                            for (const field of group.fields) {
                                if (field.hidden) continue;

                                // Buscar los campos de control en este grupo
                                if (field.question_id === controlQuestionId1) {
                                    field1Value = field.value || field.value_default || '';
                                } else if (field.question_id === controlQuestionId2) {
                                    field2Value = field.value || field.value_default || '';
                                }
                            }
                        }

                        // Determinar el tipo basado en los valores de control de este grupo
                        let mappingType = "";
                        if (field1Value && field1Value !== "") {
                            mappingType = field1Value;
                        } else if (field2Value && field2Value !== "") {
                            mappingType = field2Value;
                        }

                        // Configurar el mapeo basado en el tipo determinado para este grupo
                        let questionIdToKeyMap: { [key: string]: string } = {};

                        if (mappingType === "1") {
                            questionIdToKeyMap = {
                                "7f842c4f-b6a1-464e-90c3-2ff9ee750b3b": "beneficiary_class",
                                "773e8c82-3c69-4745-8520-43ac32b1b030": "first_name",
                                "6fe7ea08-6360-4d02-8fe5-3da568536456": "middle_name",
                                "60d2747d-e39e-4026-a98d-960a06d8fa72": "last_name",
                                "f486af1f-5859-46de-9372-2d157e6df8be": "last_name2",
                                "5bb8d0cc-e0b2-4141-9315-3b4f57ba5a86": "document_type",
                                "d3ff97ed-7cbe-4a5b-8354-a1885c44472a": "document_number",
                                "90806065-0d4c-4818-a31e-05d691195368": "birth_date",
                                "69becc7e-b21e-4fc6-8458-dff7f7ddcfa9": "type",
                                "b58fb361-e2f2-46b0-a1ae-bc66e6a0d302": "relationship",
                                "e00cd1a2-7eaf-4e36-ab0a-97dfe49ec7bc": "percent",
                                "f01b88ce-1300-48a9-9e4a-a542c3b9b361": "irrevocable",
                                "238f0c3b-a5b9-461c-a617-a6e67597a90a": "more_detail",
                            };
                        } else if (mappingType === "2") {
                            questionIdToKeyMap = {
                                "624f039a-a4ba-4494-b126-f4ba8664089e": "beneficiary_class",
                                //"43fcb9f7-fa03-4452-a6a4-591aa1fff9c7": "entity_name",
                                "43fcb9f7-fa03-4452-a6a4-591aa1fff9c7": "first_name",
                                "00976b6d-ba1a-4840-8e2f-f7c229f9cc99": "type",
                                "9b74ed99-0b2f-4142-9708-86cf100b8950": "relationship",
                                "cd65e9fa-85fb-4bc4-b6c8-013afb7e0e01": "percent",
                                "238f0c3b-a5b9-461c-a617-a6e67597a90a": "more_detail",
                                "f01b88ce-1300-48a9-9e4a-a542c3b9b361": "irrevocable",
                            };
                        }

                        // Procesar los datos de este grupo específico
                        if (group.fields && Object.keys(questionIdToKeyMap)?.length > 0) {
                            const groupData: { [key: string]: any } = {};
                            // Campos que representan datos ingresados por el usuario (no de sistema:
                            // beneficiary_class/type/relationship/irrevocable se setean solos).
                            const userValueKeys = ['first_name', 'last_name', 'document_number', 'birth_date', 'percent'];
                            let hasUserValue = false;

                            for (const field of group.fields) {
                                if (field.hidden) continue;

                                const key = questionIdToKeyMap[field.question_id];
                                if (key) {
                                    const value = field.value || field.value_default || '';
                                    groupData[key] = key === 'irrevocable' ? value === '1' : value;
                                    if (userValueKeys.includes(key) && value !== '') hasUserValue = true;
                                }
                            }

                            // Solo agregar el beneficiario si tiene al menos un dato real del usuario.
                            // Evita enviar beneficiarios vacíos al backend.
                            if (hasUserValue) {
                                results.push(groupData);
                            }
                        }
                    }
                }
            }
        }

        return results;
    };

    const getBeneficiaries = () => {
        // IDs de las secciones para Fideicomiso
        const TRUST_PRINCIPAL_SECTION = "cdae5567-3bb8-4cc5-9ff2-5399eac3b97c";
        const TRUST_CONTINGENT_SECTION = "96a1dd0d-3315-40de-983c-dc61a73a3f13";
        const REGULAR_BENEFICIARY_SECTION = "e7486edd-7b28-403c-99b9-be59e155d046";

        // Mapeo especial para el Beneficiario Principal del Fideicomiso
        const getTrustPrincipal = () => {
            const results: any[] = [];
            if (!applicationSummary) return results;
            for (const page of applicationSummary) {
                if (page.template_page_id !== "45b1f7aa-6a90-4567-b549-3c9708a03124") continue;
                if (!page.json?.sections) continue;

                for (const section of page.json.sections) {
                    if (section.section_id === TRUST_PRINCIPAL_SECTION) {
                        if (!section.groups) continue;
                        for (const group of section.groups) {
                            if (group.hidden) continue;
                            const groupData: { [key: string]: any } = {};

                            // Mapeo fijo para esta sección según lo visto
                            const questionIdToKeyMap: { [key: string]: string } = {
                                "2d81f6f7-6128-48d4-a059-dbb6a60afe2f": "beneficiary_class",
                                "b2da7e6b-2f83-4038-acb2-008582342c53": "first_name",
                                "d64e05a1-1d47-4fb3-a613-bc4c69a09e8f": "type",
                                "f4d0b44d-12aa-4487-8dfb-1509f05dfabc": "relationship",
                                "904a17ca-757e-4212-8e0c-42beea8d3c09": "percent",
                                "f01b88ce-1300-48a9-9e4a-a542c3b9b361": "irrevocable",
                                "238f0c3b-a5b9-461c-a617-a6e67597a90a": "more_detail",
                            };

                            if (group.fields) {
                                for (const field of group.fields) {
                                    const key = questionIdToKeyMap[field.question_id];
                                    if (key) {
                                        let value = field.value || field.value_default || '';

                                        // Aplicar valores en duro según requerimiento para Tipo 2
                                        if (key === "beneficiary_class") {
                                            value = "2";
                                        } else if (key === "relationship") {
                                            value = "36";
                                        } else if (key === "type") {
                                            value = "3";
                                        }

                                        groupData[key] = key === 'irrevocable' ? value === '1' : value;
                                    }
                                }
                            }
                            if (Object.keys(groupData).length > 0) {
                                results.push(groupData);
                            }
                        }
                    }
                }
            }
            return results;
        };

        // Mapeo de beneficiario principal para persona clave
        const getKeyPersonBeneficiaries = () => {
            const KEY_PERSON_BENEFICIARY_SECTION = "862d5688-dcb6-4bce-af52-0078814542ca";
            const results: any[] = [];
            if (!applicationSummary) return results;

            const questionIdToKeyMap: { [key: string]: string } = {
                "9c546891-e180-4dd4-b3d1-221faea81a10": "first_name",
                "d64e05a1-1d47-4fb3-a613-bc4c69a09e8f": "type",
                "f4d0b44d-12aa-4487-8dfb-1509f05dfabc": "relationship",
                "904a17ca-757e-4212-8e0c-42beea8d3c09": "percent",
            };

            for (const page of applicationSummary) {
                if (page.template_page_id !== "1cf974cb-1d7d-4480-83d3-dcd398b108d8") continue;
                if (!page.json?.sections) continue;

                for (const section of page.json.sections) {
                    if (section.section_id !== KEY_PERSON_BENEFICIARY_SECTION) continue;
                    if (!section.groups) continue;

                    for (const group of section.groups) {
                        if (group.hidden) continue;
                        const groupData: { [key: string]: any } = {};

                        if (group.fields) {
                            for (const field of group.fields) {
                                const key = questionIdToKeyMap[field.question_id];
                                if (key) {
                                    let value = field.value || field.value_default || '';
                                    if (key === "type") {
                                        value = "3";
                                    } else if (key === "relationship") {
                                        value = "22";
                                    }
                                    groupData[key] = value;
                                }
                            }
                        }

                        if (Object.keys(groupData).length > 0) {
                            results.push(groupData);
                        }
                    }
                }
            }
            return results;
        };

        // Normalizar porcentaje: enviar valor numérico sin símbolo "%" al API
        const normalizePercent = (beneficiaries: any[]) =>
            beneficiaries.map(b => b.percent
                ? { ...b, percent: String(parseInt(String(b.percent).replace('%', '')) || 0) }
                : b
            );

        // Persona clave: usar PAGE_KEY_PERSON_BENEFICIARIES cuando existe Q1
        const hasKeyPersonQuestionnaire = applicationSummary.some(
            p => p.template_page_id === '6d757fb1-5bed-4430-920f-56f73e3dd039'
        );
        if (hasKeyPersonQuestionnaire) {
            return normalizePercent(getKeyPersonBeneficiaries());
        }

        // Primero intentamos con beneficiarios regulares
        // 'type' viene directamente del campo: 1 = principal, 2 = contingente
        const regular = getFieldsBySectionId(REGULAR_BENEFICIARY_SECTION, "c310f869-e65e-47ad-8207-6388c9cfa9ef");
        if (regular.length > 0) {
            return normalizePercent(regular);
        }

        // Si no hay regulares, buscamos los de fideicomiso
        const trustPrincipal = getTrustPrincipal();
        // Contingentes del fideicomiso: type = 2 en hardcode
        const trustContingent = getFieldsBySectionId(TRUST_CONTINGENT_SECTION).map(b => ({
            ...b,
            type: "2",
        }));

        return normalizePercent([...trustPrincipal, ...trustContingent]);
    }

    const findGroupWithBothConditions = (sectionId: string, questionId1: string, value1: string, questionId2: string, value2: string): boolean => {
        if (!applicationSummary) return false;

        for (const page of applicationSummary) {
            if (!page.json?.sections) continue;

            for (const section of page.json.sections) {
                if (section.section_id === sectionId) {
                    if (!section.groups) continue;

                    for (const group of section.groups) {
                        if (group.hidden) continue;
                        if (!group.fields) continue;

                        let field1Found = false;
                        let field2Found = false;

                        for (const field of group.fields) {
                            if (field.hidden) continue;

                            const fieldValue = field.value || field.value_default || '';

                            if (field.question_id === questionId1 && fieldValue === value1) {
                                field1Found = true;
                            }

                            if (field.question_id === questionId2 && fieldValue === value2) {
                                field2Found = true;
                            }

                            // Verificar inmediatamente si ambas condiciones se han cumplido
                            if (field1Found && field2Found) {
                                return true;
                            }
                        }
                    }
                }
            }
        }

        return false;
    };

    const getHealthInsuranceBySectionId = (sectionId: string, excludeGroupId?: string): Array<{ company: string; coverage_amount: number | null }> => {
        const results: Array<{ company: string; coverage_amount: number | null }> = [];

        // QuestionIds para health insurance
        const companyQuestionId = "3b5d755a-0de8-4cdc-b58f-5fc789f527a0";
        const coverageAmountQuestionId = "3cb46b20-c80d-48b4-8e7d-5c2f374c4b59";

        if (!applicationSummary) return results;

        for (const page of applicationSummary) {
            if (!page.json?.sections) continue;

            for (const section of page.json.sections) {
                if (section.section_id === sectionId) {
                    if (!section.groups) continue;

                    for (const group of section.groups) {
                        if (group.hidden) continue;
                        if (excludeGroupId && group.group_id === excludeGroupId) continue;

                        let company: string | undefined;
                        let coverageAmount: string | undefined;

                        if (group.fields) {
                            for (const field of group.fields) {
                                if (field.hidden) continue;

                                if (field.question_id === companyQuestionId) {
                                    company = field.value || field.value_default || undefined;
                                } else if (field.question_id === coverageAmountQuestionId) {
                                    coverageAmount = field.value || field.value_default || undefined;
                                }
                            }
                        }

                        if (company) {
                            // Limpiar el coverageAmount eliminando comas antes de convertir a número
                            const cleanAmount = coverageAmount ? coverageAmount.replace(/,/g, '') : null;
                            const amount = cleanAmount ? Number(cleanAmount) : null;

                            results.push({
                                company: company,
                                coverage_amount: amount
                            });
                        }
                    }
                }
            }
        }

        return results;
    };

    const getOccupationBySectionId = (sectionId: string, excludeGroupId?: string): Array<{ name: string; description: string }> => {
        const results: Array<{ name: string; description: string }> = [];

        // QuestionIds para occupation
        const nameQuestionId = "4a474f62-dca5-4705-8063-4a4a37e8e9f7";
        const descriptionQuestionId = "664ac05f-bffa-4a6b-b5a1-d0530f27caae";

        if (!applicationSummary) return results;

        for (const page of applicationSummary) {
            if (!page.json?.sections) continue;

            for (const section of page.json.sections) {
                if (section.section_id === sectionId) {
                    if (!section.groups) continue;

                    for (const group of section.groups) {
                        if (group.hidden) continue;
                        if (excludeGroupId && group.group_id === excludeGroupId) continue;

                        let name: string | undefined;
                        let description: string | undefined;

                        if (group.fields) {
                            for (const field of group.fields) {
                                if (field.hidden) continue;

                                if (field.question_id === nameQuestionId) {
                                    name = field.value || field.value_default || undefined;
                                } else if (field.question_id === descriptionQuestionId) {
                                    description = field.value || field.value_default || undefined;
                                }
                            }
                        }

                        if (name) {
                            results.push({
                                name: name,
                                description: description || ""
                            });
                        }
                    }
                }
            }
        }

        return results;
    };

    const getFiles = (): Array<{ type: string; file: string; sendToSummary: boolean }> => {
        const results: Array<{ type: string; file: string; sendToSummary: boolean }> = [];
        if (!applicationSummary) return results;

        for (const page of applicationSummary) {
            if (!page.json?.sections) continue;

            for (const section of page.json.sections) {
                if (section.hidden) continue;
                if (!section.groups) continue;

                for (const group of section.groups) {
                    if (group.hidden) continue;
                    if (!group.fields) continue;

                    for (const field of group.fields) {
                        if (field.hidden) continue;
                        if (field.ui !== 'fileSearch') continue;

                        const file = field.value || field.value_default;
                        if (!file) continue;

                        const type = field.title?.es || field.title?.en || '';
                        results.push({ type, file, sendToSummary: false });
                    }
                }
            }
        }

        return results;
    };

    const getPaymentSession = async (): Promise<PaymentSessionResponse> => {
        let currentApplicationSummary = applicationSummary;

        // Si applicationSummary está vacío, cargar los datos
        if (currentApplicationSummary?.length === 0 && quoteID) {
            currentApplicationSummary = await getApplicationSummary(quoteID);
        }

        // Verificar que tenemos los datos necesarios
        if (currentApplicationSummary?.length === 0) {
            throw new Error('No se pudieron cargar los datos de la aplicación');
        }

        const paymentSessionRequest = createInitialPaymentSession();
        paymentSessionRequest.formData = {
            firstName,
            lastName,
            middleName,
            email,
            phone,
            address,
            city,
            state,
            country
        };
        paymentSessionRequest.IDQuotation = quoteID;
        paymentSessionRequest.IDApplication = currentApplicationSummary[0].quote_form_id.toString();
        paymentSessionRequest.IDPerson = generarUuid();
        paymentSessionRequest.plan = selected?.product_type ? (selected.product_type === "easy_term" ? "Easy Term" : "Master Term") : "";
        paymentSessionRequest.typePerson = typePerson;
        paymentSessionRequest.frequency = selected?.frecuency_selected;

        paymentSessionRequest.amount = lastQuote?.total_premium_in_frecuency;
        paymentSessionRequest.term = Number(selected?.term_selected);
        const paymentSessionResponse = await createPaymentSession(paymentSessionRequest);
        return paymentSessionResponse;
    };

    return (
        <MxConfirmationContext.Provider value={{
            isLoading,
            summarySource,
            coverageInformation,
            setCoverageInformation,
            personalInformationOfTheInsured,
            setPersonalInformationOfTheInsured,
            occupationInformation,
            setOccupationInformation,
            incomeInformation,
            setIncomeInformation,
            politicallyExposedPersonInformation,
            setPoliticallyExposedPersonInformation,
            riskyActivitiesInformation,
            setRiskyActivitiesInformation,
            familyMedicalHistoryInformation,
            setFamilyMedicalHistoryInformation,
            insuranceInformation,
            setInsuranceInformation,
            beneficiaryInformation,
            setBeneficiaryInformation,
            medicalInformation,
            setMedicalInformation,
            physicalInformation,
            setPhysicalInformation,
            policyOwnerInformation,
            setPolicyOwnerInformation,
            email,
            setEmail,
            firstName,
            setFirstName,
            lastName,
            setLastName,
            phone,
            setPhone,
            address,
            setAddress,
            city,
            setCity,
            state,
            setState,
            country,
            setCountry,
            zipCode,
            setZipCode,
            backToApplication,
            getPageIdByTemplateId,
            findValueByGroupAndQuestion,
            findLabelByGroupAndQuestion,
            getFieldsBySectionId,
            findGroupWithBothConditions,
            getHealthInsuranceBySectionId,
            getOccupationBySectionId,
            relationInformation,
            setRelationInformation,
            policyPurposeInformation,
            setPolicyPurposeInformation,
            substanceConsumptionInformation,
            setSubstanceConsumptionInformation,
            financialInformation,
            setFinancialInformation,
            keyPersonQuestionnaireInformation,
            setKeyPersonQuestionnaireInformation,
            getPaymentSession,
            getBeneficiaries,
            getFiles,
            refreshData
        }}>
            {children}
        </MxConfirmationContext.Provider>
    );
};

export const useMxConfirmation = () => {
    const context = useContext(MxConfirmationContext);
    if (context === undefined) {
        throw new Error('useConfirmation must be used within a ConfirmationProvider');
    }
    return context;
};

